import { json, type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { useState, type MouseEvent, type FormEvent } from 'react'
import { Icon } from '#app/components/media/icon.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.ts'
import { CodeBlock } from '#app/old/components/data-display/code-block.tsx'
import { mergeMeta } from '#app/utils/seo.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Styling } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Container',
}

export const meta: MetaFunction = mergeMeta(() => [
	{ title: `${component.name} Component | VerveUI Documentation` },
	{
		name: 'description',
		content: `This is the ${component.name} component. ${component.description.join(' ')}`,
	},
])

const component: componentProps = {
	name: 'Container',
	shortName: 'Container',
	fileName: 'container',
	category: 'layout',
	description: ["Gives content a fluid padding. If viewport goes beyond the website's max width, the content stays centered."],
	features: ['Padding flows between min and max values.', "Inherits the website's max width and centers content beyond it."],
	parts: [
		{
			name: 'Container',
			description: "Gives content a fluid padding. If viewport goes beyond the website's max width, the content stays centered.",
		},
	],
	usage: `<Container>Some content</Container>`,
	styling: `.container {
	@apply max-w-website px-viewport-padding mx-auto w-full overflow-x-hidden;
}`,
}

export const loader = async () => {
	const source = await readSource({ category: component.category, fileName: component.fileName })
	return json({ source })
}

const HeadingRoute = () => {
	const { source } = useLoaderData<typeof loader>()

	const { name, description, shortName, fileName, features, usage, styling } = component

	const [showCopyIcon, setShowCopyIcon] = useState(true)
	const [, copy] = useCopyToClipboard()

	const code = `:root {
	/* Website width */
	--website-min-width: 320; /* 👈 Unitless pixels */
	--website-max-width: 1536; /* 👈 Unitless pixels */
	
	/* Spacing */
	--container-min-padding: 6; /* 👈 Unitless pixels */
	--container-max-padding: 32; /* 👈 Unitless pixels */
}`

	const handleCopy = (e: MouseEvent<HTMLButtonElement>) => {
		copy(code)
		if (showCopyIcon) {
			setShowCopyIcon(false)
			setTimeout(() => setShowCopyIcon(true), 1000)
		}
	}

	const [websiteMinWidth, setWebsiteMinWidth] = useState(320)
	const [websiteMaxWidth, setWebsiteMaxWidth] = useState(1536)
	const [containerMinPadding, setContainerMinPadding] = useState(14)
	const [containerMaxPadding, setContainerMaxPadding] = useState(32)
	const handleWebsiteMinWidth = (e: FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget
		setWebsiteMinWidth(Number(value))
		document.documentElement.style.setProperty('--website-min-width', value)
	}
	const handleWebsiteMaxWidth = (e: FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget
		setWebsiteMaxWidth(Number(value))
		document.documentElement.style.setProperty('--website-max-width', value)
	}
	const handleContainerMinPadding = (e: FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget
		setContainerMinPadding(Number(value))
		document.documentElement.style.setProperty('--space-viewport-min-padding', value)
	}
	const handleContainerMaxPadding = (e: FormEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget
		setContainerMaxPadding(Number(value))
		document.documentElement.style.setProperty('--space-viewport-max-padding', value)
	}

	const handleClick = (e: MouseEvent<HTMLInputElement>) => {
		// Add
		// background-image: linear-gradient(to bottom, var(--color-gray-900) 0%, var(--color-gray-900) 100%), linear-gradient(to bottom, var(--color-muted-400) 0%, var(--color-muted-400) 100%);
		// background-clip: content-box, padding-box;
		// To the Container component
		const containers = document.querySelectorAll<HTMLElement>('.container')
		const contentColor = '#99BEE3'
		const paddingColor = '#BDDAB3'
		const marginColor = '#F8C69A'
		if (e.currentTarget.checked) {
			containers.forEach(container => {
				container.style.backgroundImage = `linear-gradient(to bottom, ${contentColor} 0%, ${contentColor} 100%), linear-gradient(to bottom, ${paddingColor} 0%, ${paddingColor} 100%)`
				container.style.backgroundClip = 'content-box, padding-box'
			})
			document.body.style.backgroundImage = `linear-gradient(to bottom, ${marginColor} 0%, ${marginColor} 100%), linear-gradient(to bottom, ${marginColor} 0%, ${marginColor} 100%), linear-gradient(to bottom, ${marginColor} 0%, ${marginColor} 100%)`
		} else {
			containers.forEach(container => {
				container.style.backgroundImage = 'none'
				container.style.backgroundClip = 'border-box'
			})
			document.body.style.backgroundImage = 'none'
		}
	}

	return (
		<>
			<Description name={name} description={description} />
			<H2>Usage</H2>
			<CodeBlock code={usage} filename={`${shortName}Example`} extension="tsx" className="mt-4" />
			<P>
				This page is wrapped in a <Code>{'<Container />'}</Code>. To see it in action, click this checkbox{' '}
				<input onClick={handleClick} type="checkbox" className="text-primary-500 h-4 w-4" />, edit the values below, and resize the window and see how the padding grows fluidly with
				viewport width. Also notice how the <Code>{'<Container />'}</Code> remains centered when the viewport goes beyond the website's max width of <Code>1536px</Code>.
			</P>
			<div className="mb-10 max-w-full">
				<div className="notranslate group font-code text-size-xs overflow-hidden rounded-xl bg-[#182e3f] font-semibold leading-5 text-white">
					<div className="bg-foreground/20 flex items-center justify-between pl-3">
						<div className="hidden items-center gap-2 sm:flex">
							<div className="h-[12px] w-[12px] rounded-full bg-[#FE5650]"></div>
							<div className="h-[12px] w-[12px] rounded-full bg-[#FDB43C]"></div>
							<div className="h-[12px] w-[12px] rounded-full bg-[#29C045]"></div>
						</div>
						<div className="overflow-x-hidden text-ellipsis whitespace-nowrap">styles.css</div>
						<button className="min-h-tap min-w-tap hover:bg-muted-100/10 h-11 rounded-lg py-2 px-7" onClick={handleCopy}>
							{showCopyIcon ?
								<Icon name="copy" label="Copy to clipboard" />
							:	<Icon name="check" label="Saved to clipboard" />}
						</button>
					</div>
					<div className="flex">
						<ol className="bg-foreground/10 text-muted-500 py-2 pr-4 pl-7 select-none">
							<li>1</li>
							<li>2</li>
							<li>3</li>
							<li>4</li>
							<li>5</li>
							<li>6</li>
							<li>7</li>
							<li>8</li>
							<li>9</li>
						</ol>
						<pre className="flex-1 overflow-x-auto py-2 pr-4 pl-2">
							<code>
								<div>
									<span className="text-green-400">:root </span>
									<span>{'{'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span className="text-blue-600">{'/* Website width */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--website-min-width: </span>
									<span className="text-[#FE5981]">
										<input type="number" value={websiteMinWidth} className="inline-block w-[100px] rounded-md pl-6" onChange={handleWebsiteMinWidth} />
									</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Unitless pixels */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--website-max-width: </span>
									<span className="text-[#FE5981]">
										<input type="number" value={websiteMaxWidth} className="inline-block w-[100px] rounded-md pl-6" onChange={handleWebsiteMaxWidth} />
									</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Unitless pixels */'}</span>
								</div>
								<div>&nbsp;</div>
								<div>
									<span>&#9;</span>
									<span className="text-blue-600">{'/* Spacing */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--container-min-padding: </span>
									<span>
										<input type="number" value={containerMinPadding} onChange={handleContainerMinPadding} className="inline-block w-[100px] rounded-md pl-6" />
									</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Unitless pixels */'}</span>
								</div>
								<div>
									<span>&#9;</span>
									<span>--container-max-padding: </span>
									<span>
										<input type="number" value={containerMaxPadding} onChange={handleContainerMaxPadding} className="inline-block w-[100px] rounded-md pl-6" />
									</span>
									<span>; </span>
									<span className="text-blue-600">{'/* 👈 Unitless pixels */'}</span>
								</div>
								<div>
									<span>{'}'}</span>
								</div>
							</code>
						</pre>
					</div>
				</div>
			</div>
			<Features features={features} />

			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default HeadingRoute
